// Iter 71 — Vendor Master · List view (Phase 1).
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Plus, MagnifyingGlass, AddressBook, CaretLeft, CaretRight,
    Check, X, ArrowRight, Funnel,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { CATEGORY_LABEL, DEFAULT_ENUMS } from "./vendor_constants";

const PAGE_SIZE = 25;

export default function VendorList() {
    const { can } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();

    // URL-state-backed filters (parity with Clients/AMC/Payments)
    const q = params.get("q") || "";
    const category = params.get("category") || "";
    const statusFilter = params.get("status") || "all";
    const sort = params.get("sort") || "recently_added";
    const page = Math.max(1, parseInt(params.get("page") || "1", 10));

    const [searchInput, setSearchInput] = useState(q);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value === "" || value === null || value === undefined) {
            next.delete(key);
        } else {
            next.set(key, value);
        }
        // Any filter change resets pagination.
        if (key !== "page") next.delete("page");
        setParams(next, { replace: false });
    };

    // Fetch on filter / pagination change. `.then()` chain pattern from
    // RepositoryDashboard.jsx — setState is NEVER called synchronously
    // inside the effect body (that would trip react-hooks/set-state-in-effect).
    // We rely on `loading` already being true initially and the `.finally()`
    // flipping it off. During refetches the old rows stay visible momentarily
    // — acceptable UX trade-off for a lint-clean useEffect.
    useEffect(() => {
        let cancelled = false;
        api.get("/vendors", {
            params: {
                q: q || undefined,
                category: category || undefined,
                status_filter: statusFilter,
                sort,
                page,
                page_size: PAGE_SIZE,
            },
        })
            .then(({ data }) => {
                if (cancelled) return;
                setRows(data.items || []);
                setTotal(data.total || 0);
                setPages(data.pages || 0);
            })
            .catch((err) => {
                if (!cancelled) toast.error(formatApiError(err, "Failed to load vendors"));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [q, category, statusFilter, sort, page, reloadKey]);

    // Debounced search → URL
    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput !== q) updateParam("q", searchInput);
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput, q]);

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="vendor-list-page">
            <PageHeader
                eyebrow="Vendor Management"
                title="Vendors"
                description="Maintain service providers, consultants, technology vendors, auditors, API providers, hosting partners and business associates."
                actions={can("vendor:write") && (
                    <Button
                        data-testid="vendor-new-btn"
                        onClick={() => navigate("/vendors/new")}
                        className="gap-2"
                    >
                        <Plus size={16} weight="bold" />
                        Add Vendor
                    </Button>
                )}
            />

            {/* Filters row */}
            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="vendor-filter-row">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        data-testid="vendor-search-input"
                        placeholder="Search name, code, contact, email, GSTIN…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <select
                    data-testid="vendor-filter-category"
                    value={category}
                    onChange={(e) => updateParam("category", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
                >
                    <option value="">All categories</option>
                    {DEFAULT_ENUMS.categories.map((c) => (
                        <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>
                    ))}
                </select>
                <select
                    data-testid="vendor-filter-status"
                    value={statusFilter}
                    onChange={(e) => updateParam("status", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
                >
                    <option value="all">All statuses</option>
                    <option value="active">Active only</option>
                    <option value="inactive">Inactive only</option>
                </select>
                <select
                    data-testid="vendor-sort"
                    value={sort}
                    onChange={(e) => updateParam("sort", e.target.value)}
                    className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white ml-auto"
                >
                    <option value="recently_added">Recently added</option>
                    <option value="recently_updated">Recently updated</option>
                    <option value="name_asc">Name A → Z</option>
                    <option value="code_asc">Code A → Z</option>
                </select>
            </div>

            {/* Table */}
            <div className="mt-5 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="vendor-table">
                        <thead className="bg-slate-50 text-slate-600">
                            <tr>
                                <th className="text-left font-medium px-4 py-3">Code</th>
                                <th className="text-left font-medium px-4 py-3">Vendor</th>
                                <th className="text-left font-medium px-4 py-3">Category</th>
                                <th className="text-left font-medium px-4 py-3">Contact</th>
                                <th className="text-left font-medium px-4 py-3">GSTIN</th>
                                <th className="text-left font-medium px-4 py-3">Currency</th>
                                <th className="text-left font-medium px-4 py-3">Status</th>
                                <th className="text-left font-medium px-4 py-3">Added</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={9} className="text-center text-slate-400 py-12">Loading…</td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="py-16">
                                        <EmptyState
                                            icon={<AddressBook size={32} className="text-slate-400" />}
                                            title="No vendors yet"
                                            message={can("vendor:write")
                                                ? "Click 'Add Vendor' to onboard your first service provider."
                                                : "No vendors have been added to the system yet."}
                                        />
                                    </td>
                                </tr>
                            )}
                            {!loading && rows.map((v) => (
                                <tr
                                    key={v.id}
                                    data-testid={`vendor-row-${v.code}`}
                                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => navigate(`/vendors/${v.id}`)}
                                >
                                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{v.code}</td>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-slate-900">{v.name}</div>
                                        {v.contact_person && (
                                            <div className="text-xs text-slate-500 mt-0.5">{v.contact_person}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">
                                        {CATEGORY_LABEL[v.category] || v.category}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">
                                        <div className="text-xs">{v.email || "—"}</div>
                                        <div className="text-xs text-slate-500">{v.mobile || ""}</div>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-slate-600">
                                        {v.gstin || <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-slate-700">{v.currency || "INR"}</td>
                                    <td className="px-4 py-3">
                                        {v.active ? (
                                            <Badge data-testid={`vendor-status-active-${v.code}`} className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1">
                                                <Check size={10} weight="bold" /> Active
                                            </Badge>
                                        ) : (
                                            <Badge data-testid={`vendor-status-inactive-${v.code}`} variant="secondary" className="bg-slate-100 text-slate-500 hover:bg-slate-100 gap-1">
                                                <X size={10} weight="bold" /> Inactive
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-500">{formatDate(v.created_at)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <ArrowRight size={14} className="text-slate-400" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination footer */}
                {!loading && total > 0 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-600">
                        <div data-testid="vendor-pagination-summary">
                            Showing {(page - 1) * PAGE_SIZE + 1}
                            –{Math.min(page * PAGE_SIZE, total)} of {total}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                data-testid="vendor-page-prev"
                                size="sm"
                                variant="outline"
                                disabled={page <= 1}
                                onClick={() => updateParam("page", page - 1)}
                            >
                                <CaretLeft size={14} />
                            </Button>
                            <span className="px-2">Page {page} of {pages}</span>
                            <Button
                                data-testid="vendor-page-next"
                                size="sm"
                                variant="outline"
                                disabled={page >= pages}
                                onClick={() => updateParam("page", page + 1)}
                            >
                                <CaretRight size={14} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
